












































































































import agora from '@/services/Agora'
import { analytics, getUserId } from '@/firebaseApp'
import rooms, { Room } from '@/services/Rooms'
import AgoraView from '@/components/global/AgoraView.vue'
import AccessCodes, { AccessCodesStates } from '@/services/AccessCodes'
import Seatings from '@/services/Seatings'
import Guests, { Guest, GuestList, GuestState } from '@/services/Guests'
import restaurants, { RestaurantPublic } from '@/services/Restaurants'
import EventBus, { eventTypes } from '@/utils/EventBus'

import Loader from '@/components/global/loader.vue'
import CallWaiter from '@/components/guest/CallWaiter.vue'
import RestaurantAmbience from '@/components/guest/RestaurantAmbience.vue'
import FullScreen from '@/components/guest/FullScreen.vue'
import CheckIn from '@/components/guest/CheckIn.vue'
import DebugTableLayout from '@/components/guest/DebugTableLayout.vue'
import WaiterVideoStream from '@/components/guest/WaiterVideoStream.vue'
import KeepAwake from '@/components/guest/KeepAwake.vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import ThankYou from '@/components/guest/ThankYou.vue'
import Offline from '@/components/screens/Offline.vue'
import PrivateVideoStream from '@/components/guest/PrivateVideoStream.vue'

Component.registerHooks(['beforeRouteEnter'])

@Component({
  components: {
    PrivateVideoStream,
    Offline,
    ThankYou,
    KeepAwake,
    WaiterVideoStream,
    DebugTableLayout,
    CheckIn,
    FullScreen,
    RestaurantAmbience,
    CallWaiter,
    Loader,
    AgoraView,
  },
})
export default class Seating extends Vue {
  @Prop({ required: true }) public accessCode!: string

  public name: string = ''
  public waiterId: string | null = null
  public waiterName: string | null = null
  public state: GuestState = GuestState.Welcome
  public calledWaiter = false
  public isSwitchingRoom = false
  public canSeeItself = false
  public guestStreamClass = 'local-center'
  public waiterStreamClass: 'waiter-center' | 'waiter-side' = 'waiter-side'
  public availableGuests: Guest[] = [] as Guest[]
  public guestList: GuestList | null = null
  public roomId: string | null = null
  public room = null as Room | null
  public restaurantId = ''
  public restaurant = null as RestaurantPublic | null
  public debug = null as number | null

  public artistVideoId: string = ''
  public artistVideoIsPlaying: boolean = false

  public enableAmbienceSound = true
  public isOnline = true
  public waiterMessage = ''
  public serviceTimeout = 0
  public watchingVideoId = null as string | null
  public youtubePlaying = false

  // @ts-ignore
  public async beforeRouteEnter(to, from, next) {
    if (to.query.debug) {
      next()
    }

    // dont' allow first time deeplinks per seating.
    // for the privacy statement and permission checks
    const restaurantId = to.params.restaurantId
    const accessCode = to.params.accessCode
    const roomId = to.query.rid

    // If we have a roomId, it's most likely a returning guest, who just gave the waiter a tip
    // So skip the deeplink check
    if (!roomId) {
      const localStorageKey = `accescode-${restaurantId}`
      const localStorageObjStr = window.localStorage.getItem(localStorageKey)
      const localStorageObj = JSON.parse(localStorageObjStr + '')
      if (!localStorageObjStr || localStorageObj.code !== accessCode) {
        next({
          name: 'submitAccessCode',
          params: { lang: to.params.lang, restaurantId },
          query: { accessCode },
        })
      }
    }

    await getUserId()

    // Check if the code is valid
    // The users could have come here from a deeplink or refresh
    const code = to.params.accessCode.toUpperCase()
    const status = await AccessCodes.check(to.params.restaurantId, code)
    if (status !== AccessCodesStates.Ok) {
      next({
        name: 'submitAccessCode',
        params: { lang: to.params.lang, restaurantId: to.params.restaurantId },
      })
    } else {
      next()
    }
  }

  public async created() {
    this.debug = parseInt(this.$route.query.debug + '', 10) || null

    // Continue when agora is ready...
    await agora.isReady()

    // Get Restaurant details
    this.restaurantId = this.$route.params.restaurantId
    this.restaurant = await restaurants.getPublicRestaurant(this.restaurantId)

    // Create the state for the new Guest
    const userId = await getUserId()
    const isReturningGuest = !!this.$route.query.r
    this.roomId = this.$route.query.rid ? this.$route.query.rid + '' : null
    const guest = await Guests.createGuest(
      this.restaurantId,
      this.accessCode,
      userId,
      this.roomId,
      isReturningGuest
    )

    // If the guest was here before, use it's name
    if (guest.name) {
      this.name = guest.name
    }

    if (this.roomId && guest.room === this.roomId) {
      await this.updateRoom(this.roomId)
    } else if (this.roomId && guest.room !== this.roomId && isReturningGuest) {
      // The guest came back after tipping, but the room is full
      // redirect to the service page
      this.redirectToThankYou(null)
    }

    // We store separate properties of guest instead of the whole guest object
    // to prevent superfluous updating of the client ui
    await Seatings.observeGuest(this.restaurantId, this.accessCode, async guest => {
      if (!guest) {
        return
      }

      this.updateCalledWaiter(guest.calledWaiter)
      this.updateState(guest.state, guest.canSeeItself, guest.waiterId, guest.waiterName)
      this.updateWaiterMessage(guest.message ? guest.message : '')
      await this.updateRoom(guest.room ? guest.room : null)
      await this.updateSessionEnded(guest)
      await this.updateSessionRefreshed(guest)
      await this.updatePlayingVideo(guest)
    })

    EventBus.$on(eventTypes.streamAdded, this.updateGuests)
  }

  public updated() {
    if (this.isSwitchingRoom) {
      return
    }

    switch (this.state) {
      case 'service':
        console.log(`[Seating.vue] play waiter`)
        agora.play(this.waiterVideoId)
        break
      case 'returning':
      case 'idle':
        console.log(`[Seating.vue] stop waiter`)
        agora.stop(this.waiterVideoId)
    }
  }

  public async beforeDestroy() {
    await this.leavePage()
  }

  mounted() {
    this.youtubePlaying = false
    window.addEventListener('beforeunload', this.promptBeforeLeave)
    window.addEventListener('unload', this.leavePage)
    window.addEventListener('online', () => this.handleNetworkChange())
    window.addEventListener('offline', () => this.handleNetworkChange())
  }

  promptBeforeLeave(e: Event) {
    // FF
    e.preventDefault()
    // @ts-ignore, Chrome wants it this way
    e.returnValue = ''
  }

  async leavePage() {
    window.removeEventListener('beforeunload', this.promptBeforeLeave)
    window.removeEventListener('unload', this.leavePage)
    window.removeEventListener('online', () => this.handleNetworkChange())
    window.removeEventListener('offline', () => this.handleNetworkChange())
  }

  public handleNetworkChange() {
    this.isOnline = navigator.onLine
  }

  public get isIOS() {
    if (/iPad|iPhone|iPod/.test(navigator.platform)) {
      return true
    } else {
      return (
        navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 2 &&
        /MacIntel/.test(navigator.platform)
      )
    }
  }

  public async joinAgora(roomId: string) {
    console.info('Guest', this.name, 'is joining agora')

    const userId = await getUserId()
    const channelId = `${this.restaurantId}:${this.accessCode}:${roomId}`
    await agora.join(channelId, userId)

    // Signal we're ready to stream
    Guests.setGuestOnAgora(this.restaurantId, this.accessCode, userId, true)

    Guests.observeActiveGuests(this.restaurantId, this.accessCode, guests => {
      this.guestList = guests
      this.updateGuests()
    })
  }

  public async updateGuests() {
    const userId = await getUserId()
    const availableGuests: Guest[] = []

    // There is no room or the room is private.
    // So there are no other guests
    if (!this.roomId || !this.room || this.room.private || !this.guestList) {
      this.availableGuests = availableGuests
      return
    }

    for (let id in this.guestList) {
      // Skip yourself
      if (id === userId) {
        continue
      }

      const guest = this.guestList[id]
      if (guest.room === this.roomId) {
        agora.streamAudio(id, true)
        this.setGuestVolume(id, guest)
        availableGuests.push(guest)
      }
    }

    this.availableGuests = availableGuests
  }

  public setGuestVolume(guestId: string, guest: Guest) {
    if (this.state === 'service' || this.state === 'watchingVideo') {
      // If I'm in service or watching a video, mute everybody else for me
      agora.setVolume(guestId, 0)
    } else if (guest.state === 'service' || guest.state === 'watchingVideo') {
      // If this guest is in service or watching a video, mute for me
      agora.setVolume(guestId, 0)
    } else if (guest.isWaiter) {
      // The waiter joined us, always turn up it's volume
      agora.setVolume(guestId, 100)
    } else if (this.artistVideoIsPlaying) {
      // The artist stream is on, turn down the guests volume
      agora.setVolume(guestId, 5)
    } else {
      // In every other case, try to respect the guestVolume setting
      agora.setVolume(guestId, this.room ? this.room.guestVolume : 100)
    }
  }

  public updateCalledWaiter(calledWaiter: boolean) {
    this.calledWaiter = calledWaiter
  }

  public updateWaiterMessage(message: string) {
    this.waiterMessage = message
  }

  public async updateSessionEnded(guest: Guest) {
    if (!guest.sessionEnded || !guest.waiterId) {
      return
    }

    // reset the session state for when the use wants to go back to the lobby
    await Guests.setEndGuestSession(
      this.restaurantId,
      this.accessCode,
      guest.id,
      guest.waiterId,
      guest.waiterName || '',
      false
    )

    this.redirectToThankYou(guest.waiterName || '')
  }

  public async updateSessionRefreshed(guest: Guest) {
    if (!guest.refreshUser) {
      return
    }

    // remove the "Are you sure" prompt
    window.removeEventListener('beforeunload', this.promptBeforeLeave)

    // reset the session state for when the use wants to go back to the lobby
    await Guests.setGuestRefreshSession(this.restaurantId, this.accessCode, guest.id, false)

    window.location.reload()
  }

  public async updatePlayingVideo(guest: Guest) {
    if (
      !guest ||
      (guest.watchingVideoId === this.watchingVideoId && guest.state === GuestState.WatchingVideo)
    ) {
      return
    }

    this.watchingVideoId = guest.watchingVideoId || null
    this.state = guest.state
  }

  public redirectToThankYou(waiterName: string | null) {
    // remove the "Are you sure" prompt
    window.removeEventListener('beforeunload', this.promptBeforeLeave)

    const query = {} as { [index: string]: any }
    if (waiterName) {
      query['waiter-name'] = waiterName
    }

    if (!!this.$route.query.r) {
      query.r = 1
    }

    window.location.href = this.$router.resolve({
      name: 'thankyou',
      params: {
        lang: this.$route.params.lang,
        restaurantId: this.restaurantId,
        accessCode: this.accessCode,
      },
      query,
    }).href
  }

  public async updateRoom(roomId: string | null) {
    if (this.roomId === roomId) {
      return
    }
    this.isSwitchingRoom = true
    this.roomId = roomId

    if (!roomId) {
      this.room = null
    } else {
      rooms.observeRoom(this.restaurantId, roomId, room => {
        // We may still receive updates from previous rooms, only update current room
        if (roomId === this.roomId) {
          this.room = room
          this.room.id = roomId
        }
      })

      if (!this.roomId) {
        return
      }

      Seatings.observeSeatingRoomState(
        this.restaurantId,
        this.accessCode,
        this.roomId,
        async roomState => {
          this.artistVideoIsPlaying = roomState?.videoIsPlaying || false
          this.artistVideoId = roomState?.videoId || ''

          this.updateGuests()
        }
      )
    }

    const userId = await getUserId()
    const roomLabel = roomId || 'lobby'
    const channelId = `${this.restaurantId}:${this.accessCode}:${roomLabel}`
    this.isSwitchingRoom = false

    await agora.join(channelId, userId, userId)
  }

  public updateState(
    state: GuestState,
    canSeeItself: boolean,
    waiterId?: string,
    waiterName?: string
  ) {
    // first cancel any timeouts
    // when the waiter initiated a 1-on-1 and immediately cancels it for instance
    clearTimeout(this.serviceTimeout)

    // Check if anything has changed
    // If not, stop
    if (this.state == state && this.canSeeItself == canSeeItself) {
      return
    }

    if (waiterId) {
      this.waiterId = waiterId
    }

    this.canSeeItself = canSeeItself
    const youTubeRef = this.$refs.youtube as any
    const youTubePlayer = youTubeRef?.player

    switch (state) {
      case 'service':
        // play sound
        // @ts-ignore
        const audio = new Audio(require('@/assets/waiterishere.mp3'))
        audio.play()

        //make the waiter appear
        this.serviceTimeout = setTimeout(() => {
          // Guest is being serviced by waiter
          agora.streamAudio(this.waiterVideoId, true)

          // So mute the playing YouTube video
          if (youTubePlayer) {
            youTubePlayer.setVolume(0)
          }

          // Only show guest stream if waiter says so
          // Else we're having it invisible
          this.state = state
          this.waiterName = waiterName || this.$i18n.t('global.waiter') + ''
          this.guestStreamClass = canSeeItself ? 'local-center' : 'local-invisible'
          this.waiterStreamClass = canSeeItself ? 'waiter-side' : 'waiter-center'
        }, 1000)
        break
      case 'returning':
      case 'idle':
        // Guest is not being attended (anymore)
        agora.streamAudio(this.waiterVideoId, false)

        // So if youtube was playing, rest the volume to what it was.
        if (youTubePlayer) {
          youTubePlayer.setVolume(this.room?.artistVolume || 100)
        }

        this.state = state
        this.guestStreamClass = 'local-invisible'
        break
      case 'welcome':
        // Guest is waiting to be seated/entering name
        this.state = state
        this.waiterName = waiterName || this.$i18n.t('global.waiter') + ''
        this.guestStreamClass = 'local-center'
        this.waiterStreamClass = 'waiter-side'
        break
    }
  }

  public get waiterVideoId() {
    return `waiter-${this.waiterId}`
  }

  public get enteredLobby() {
    return !!this.name
  }

  public get seatingWelcomeCopy() {
    if (!this.restaurant?.seatingWelcomeCopy) {
      return `${this.$i18n.t('seating.welcome')}`.replace('[x]', this.name)
    }

    return this.restaurant.seatingWelcomeCopy.replace('[x]', this.name)
  }
  // When autoplay is not supported,
  // the user should have an option to start the video
  public startVideo() {
    if (this.artistVideoIsPlaying && !this.youtubePlaying) {
      const youTubeRef = this.$refs.youtube as any
      const youTubePlayer = youTubeRef?.player
      youTubePlayer.playVideo()
      this.youtubePlaying = true
    }
  }

  @Watch('artistVideoIsPlaying')
  handleArtistVideoIsPlaying() {
    if (this.artistVideoIsPlaying) {
      // Disable ambient sound
      this.enableAmbienceSound = false

      // Set YouTube volume
      const youTubeRef = this.$refs.youtube as any
      const youTubePlayer = youTubeRef?.player
      if (youTubePlayer) {
        youTubePlayer.setVolume(this.room?.artistVolume || 100)
      }
    } else {
      this.enableAmbienceSound = true
      this.youtubePlaying = false
    }
  }

  @Watch('name')
  async handleNameChange() {
    const isLobbyFull = await Seatings.isLobbyFull(this.restaurantId, this.accessCode)
    if (this.name && !isLobbyFull) {
      this.joinAgora(this.roomId ? this.roomId : 'lobby')
    }
  }

  @Watch('room.isEndRoom')
  async toggleLeaveProtection() {
    if (this.room?.isEndRoom) {
      window.removeEventListener('beforeunload', this.promptBeforeLeave)
    } else {
      window.addEventListener('beforeunload', this.promptBeforeLeave)
    }
  }

  @Watch('isOnline')
  async handleOnlineSwitch(newVal: boolean, oldVal: boolean) {
    // When user went offline, and comes back, add it to the lobby by
    if (newVal === true && oldVal === false) {
      const userId = await getUserId()
      // Log this event, so we keep track of how often this happens
      analytics.logEvent('guest_reconnect', {
        restaurantId: this.restaurantId,
        accessCode: this.accessCode,
        userId,
      })

      // remove the "Are you sure" prompt
      window.removeEventListener('beforeunload', this.promptBeforeLeave)
      location.reload()
    }
  }
}
